import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { DropdownMenu } from '../ui/DropdownMenu';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { PipelineStageIndicator } from './PipelineStageIndicator';
import { cn } from '../../lib/utils';
import { RotateCcw, Link, Trash2, MoreVertical, Eye } from 'lucide-react';
import { useArchivePanel, ArchiveHeader } from './ArchivePanel';
import { buildRunDisplayTitle, formatRunStartedAt } from './active-runs.utils';

export type AiRunStatusFilter = 'all' | 'active' | 'completed' | 'failed';

interface RunRecord {
  runId: string;
  streamId: string;
  workflowType: string;
  displayTitle?: string;
  contextId: string;
  currentStage: string;
  workItemId?: number;
  triggeredBy: string;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
  tokensUsed?: { input: number; output: number };
  estimatedCostUsd?: number;
  metadata?: Record<string, any>;
}

interface ActiveRunsTabProps {
  streamId: string;
  refreshKey?: number;
  onStageClick?: (runId: string, stage: string) => void;
  onInspectRun?: (runId: string) => void;
  onResumeClick?: (runId: string) => void;
  onDeleteRun?: (runId: string) => void;
}

function formatElapsed(startedAt: string, endedAt?: string): string {
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

function runStatusLabel(stage: string): { label: string; variant: 'default' | 'success' | 'destructive' | 'outline' } {
  if (stage === 'complete') return { label: 'Completed', variant: 'success' };
  if (stage === 'failed') return { label: 'Failed', variant: 'destructive' };
  if (stage === 'escalated') return { label: 'Escalated', variant: 'outline' };
  return { label: 'Active', variant: 'default' };
}

const FILTER_OPTIONS: { value: AiRunStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

export function ActiveRunsTab({ streamId, refreshKey, onStageClick, onInspectRun, onResumeClick, onDeleteRun }: ActiveRunsTabProps) {
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AiRunStatusFilter>('all');
  const [archiveRuns, setArchiveRuns] = useState<RunRecord[]>([]);

  useActivityHeartbeat('cache:ai-workflow-runs', true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (streamId) params.set('streamId', streamId);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const qs = params.toString();
      const path = qs ? `/api/ai-workflows/runs?${qs}` : '/api/ai-workflows/runs';
      const data = await apiClient.get<{ runs: RunRecord[]; total: number }>(path);
      setRuns(data.runs || []);
      setTotal(data.total ?? (data.runs?.length ?? 0));
    } catch (e: unknown) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load runs');
      setRuns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [streamId, statusFilter, refreshKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useCacheEvents({
    cacheNames: ['ai-workflow-runs'],
    onEvent: () => {
      void load();
    },
  });

  const archivePanel = useArchivePanel({
    onSearch: async (from, to) => {
      const result = await apiClient.getAiRunsArchive({
        from,
        to,
        streamId: streamId || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      setArchiveRuns(result.runs || []);
    },
  });

  const displayRuns = archivePanel.archiveMode ? archiveRuns : runs;
  const displayTotal = archivePanel.archiveMode ? archiveRuns.length : total;

  const filterPills = useMemo(() => FILTER_OPTIONS, []);
  const isResumable = (run: RunRecord) =>
    run.currentStage === 'failed' || run.metadata?.stalledAtStage;
  const resumedFrom = (run: RunRecord) =>
    run.metadata?.resumedFromRunId as string | undefined;

  return (
    <div className="space-y-3">
      <ArchiveHeader label="Runs" count={displayTotal} panel={archivePanel} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
        <div className="flex flex-wrap gap-1.5">
          {filterPills.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                statusFilter === opt.value
                  ? 'bg-blue-600 text-white dark:bg-blue-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {displayTotal} run{displayTotal === 1 ? '' : 's'}
        </span>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
          <Skeleton className="h-36 w-full rounded-lg" />
          <Skeleton className="h-36 w-full rounded-lg" />
          <Skeleton className="h-36 w-full rounded-lg" />
        </div>
      ) : displayRuns.length === 0 ? (
        <Card className="border-dashed border-gray-300 dark:border-gray-600">
          <CardContent className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {archivePanel.archiveMode ? 'No runs found in this date range.' : 'No workflow runs match this filter.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
          {displayRuns.map((run) => {
            const status = runStatusLabel(run.currentStage);
            const elapsed = formatElapsed(
              run.startedAt,
              run.completedAt || (run.currentStage === 'complete' || run.currentStage === 'failed' ? run.updatedAt : undefined),
            );
            const workflowTypeLabel = String(run.workflowType || 'unknown').replace(/_/g, ' ');
            const streamLabel = String(run.streamId || 'unknown');
            const displaySteps = run.metadata?.pipelineProgress?.stages;
            const resumed = resumedFrom(run);
            const title = buildRunDisplayTitle(run);
            const startedAtLabel = formatRunStartedAt(run.startedAt);

            return (
              <Card
                key={run.runId}
                className="border-gray-200 dark:border-gray-700"
              >
                <CardHeader className="px-3 py-2">
                  <div className="flex items-start justify-between gap-1.5">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {title}
                      </CardTitle>
                      <p className="truncate font-mono text-[10px] text-gray-400 dark:text-gray-500">{run.runId}</p>
                    </div>
                    <Badge variant={status.variant} className="shrink-0 text-[10px]">{status.label}</Badge>
                    <DropdownMenu
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      }
                      items={[
                        ...(onInspectRun
                          ? [{
                              label: 'Inspect',
                              icon: <Eye className="h-4 w-4" />,
                              onClick: () => onInspectRun(run.runId),
                            }]
                          : []),
                        ...(isResumable(run) && onResumeClick
                          ? [{
                              label: 'Resume',
                              icon: <RotateCcw className="h-4 w-4" />,
                              onClick: () => onResumeClick(run.runId),
                            }]
                          : []),
                        ...(onDeleteRun && ['complete', 'failed', 'escalated'].includes(run.currentStage)
                          ? [{
                              label: 'Delete',
                              icon: <Trash2 className="h-4 w-4" />,
                              className: 'text-red-600 dark:text-red-400',
                              onClick: () => onDeleteRun(run.runId),
                            }]
                          : []),
                      ]}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 px-3 pb-2.5 pt-0">
                  <PipelineStageIndicator
                    currentStage={run.currentStage}
                    stalledAtStage={run.metadata?.stalledAtStage}
                    displaySteps={displaySteps}
                    onStageClick={onStageClick ? (stage) => onStageClick(run.runId, stage) : undefined}
                  />
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{workflowTypeLabel}</Badge>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{streamLabel}</Badge>
                    <span>{elapsed}</span>
                    <span>Started {startedAtLabel}</span>
                    <span className="truncate max-w-[120px]">{run.triggeredBy}</span>
                    {run.tokensUsed && (
                      <span>{(run.tokensUsed.input + run.tokensUsed.output).toLocaleString()} tok</span>
                    )}
                  </div>
                  {resumed && (
                    <div className="flex items-center gap-2 pt-0.5">
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 dark:text-blue-400">
                        <Link className="h-3 w-3" />
                        Resumed from {resumed.slice(0, 12)}...
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
